import { Schema } from "effect";

export const MCP_PROTECTED_RESOURCE_PATH = "/.well-known/oauth-protected-resource";
export const MCP_PROTECTED_RESOURCE_PATHS = [MCP_PROTECTED_RESOURCE_PATH, `${MCP_PROTECTED_RESOURCE_PATH}/mcp`] as const;

export const McpOAuthConfig = /* @__PURE__ */ (() => Schema.Struct({
  resource: Schema.String,
  audience: Schema.String,
  issuer: Schema.NullOr(Schema.String),
  authorizationServers: Schema.Array(Schema.String),
  jwksUrl: Schema.NullOr(Schema.String),
  hs256Secret: Schema.NullOr(Schema.Redacted(Schema.String, { disallowJsonEncode: true })),
  requiredScopes: Schema.Array(Schema.String),
  scopesSupported: Schema.Array(Schema.String),
  serviceDocumentation: Schema.NullOr(Schema.String),
  clockSkewSeconds: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 600 }))
}))();
export type McpOAuthConfig = typeof McpOAuthConfig.Type;

export const McpProtectedResourceMetadata = /* @__PURE__ */ (() => Schema.Struct({
  resource: Schema.String,
  bearer_methods_supported: Schema.Tuple([Schema.Literal("header")]),
  authorization_servers: Schema.optionalKey(Schema.Array(Schema.String)),
  scopes_supported: Schema.optionalKey(Schema.Array(Schema.String)),
  resource_documentation: Schema.optionalKey(Schema.String)
}))();
export type McpProtectedResourceMetadata = typeof McpProtectedResourceMetadata.Type;

// A verified access token authenticates the MCP transport only. It never
// supplies the station principal; station authorization has its own owner.
export const McpAuthClaims = /* @__PURE__ */ (() => Schema.Struct({
  subject: Schema.NullOr(Schema.String),
  scopes: Schema.Array(Schema.String),
  clientId: Schema.NullOr(Schema.String),
  issuer: Schema.NullOr(Schema.String),
  expiresAt: Schema.NullOr(Schema.Number),
  raw: Schema.Record(Schema.String, Schema.Unknown)
}))();
export type McpAuthClaims = typeof McpAuthClaims.Type;
export const McpTokenVerification = /* @__PURE__ */ (() => Schema.Union([
  Schema.Struct({ ok: Schema.Literal(true), claims: McpAuthClaims }),
  Schema.Struct({ ok: Schema.Literal(false), error: Schema.Literals(["invalid_token", "insufficient_scope"]), description: Schema.String })
]))();
export type McpTokenVerification = typeof McpTokenVerification.Type;

export const McpJwtSegments = /* @__PURE__ */ (() => Schema.Tuple([Schema.String, Schema.String, Schema.String]))();
// JWT segments use unpadded base64url. The general Effect codec also accepts
// padding/CRLF, which would broaden the deployed token acceptance boundary.
export const McpJwtBytes = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]*$/)).pipe(Schema.decodeTo(Schema.Uint8ArrayFromBase64Url)))();
export const McpJwtObject = /* @__PURE__ */ (() => Schema.Record(Schema.String, Schema.Unknown))();
export const McpJwtAudience = /* @__PURE__ */ (() => Schema.Union([Schema.String, Schema.Array(Schema.String)]))();
export const McpJwtScopes = /* @__PURE__ */ (() => Schema.Union([Schema.String, Schema.Array(Schema.String)]))();
export const McpJwtAlgorithm = /* @__PURE__ */ (() => Schema.Literals(["HS256", "RS256", "RS384", "RS512", "ES256", "ES384"]))();
export type McpJwtAlgorithm = typeof McpJwtAlgorithm.Type;
export const McpJwk = /* @__PURE__ */ (() => Schema.Record(Schema.String, Schema.Unknown))();
export type McpJwk = typeof McpJwk.Type;
